// 🔹 Base URLs
const PROMOFLOW_BASE_API_URL = {
  promoflowUrl: import.meta.env.VITE_API_URL,
  authUrl: import.meta.env.VITE_AUTH_API_URL,
  restaurantUrl: import.meta.env.VITE_RESTAURANT_API,
  triggerUrl: import.meta.env.VITE_TRIGGER_URL,
  promoflowV2Url: import.meta.env.VITE_PROMOFLOW_V2_API_URL,
  integrationsUrl: import.meta.env.VITE_INTEGRATIONS_URL,
  promoflowHubUrl: import.meta.env.VITE_PROMOFLOW_HUB_API_URL,
  sseMonitorUrl: import.meta.env.VITE_SSE_MONITOR_URL,
  franchiseUrl: import.meta.env.VITE_FRANCHISE_API,
  voiceAIUrl: import.meta.env.VITE_VOICE_AI_URL,
} as const;

// 🔹 Destructure (CLEAN USAGE)
const {
  promoflowUrl,
  authUrl,
  restaurantUrl,
  triggerUrl,
  promoflowV2Url,
  integrationsUrl,
  promoflowHubUrl,
  sseMonitorUrl,
  franchiseUrl,
  voiceAIUrl,
} = PROMOFLOW_BASE_API_URL;

// 🔹 Integration Base
const INTEGRATION_BASE = `${integrationsUrl}`;

// 🔹 Channel Config (API-aware design)
const CHANNEL_CONFIG = {
  facebook: { hasAuth: true, hasPages: true, hasUpdate: true },
  instagram: { hasAuth: true, hasPages: true, hasUpdate: true },
  "instagram-direct": { hasAuth: true, hasPages: false, hasUpdate: false },
  tiktok: { hasAuth: true, hasPages: false, hasUpdate: false },
  email: { hasAuth: false, hasPages: false, hasUpdate: false, hasActivate: true },
  sms: { hasAuth: false, hasPages: false, hasUpdate: false, hasActivate: true },
} as const;

type Channel = keyof typeof CHANNEL_CONFIG;

//Integration Endpoints (SAFE + DYNAMIC)
const INTEGRATION_ENDPOINTS = {
  authorize: (channel: Channel) =>
    CHANNEL_CONFIG[channel]?.hasAuth
      ? `${INTEGRATION_BASE}/${channel}/authorize`
      : null,

  authenticate: (channel: Channel) =>
    CHANNEL_CONFIG[channel]?.hasAuth
      ? `${INTEGRATION_BASE}/${channel}/authenticate`
      : null,

  fetchPages: (channel: Channel, id: string) =>
    CHANNEL_CONFIG[channel]?.hasPages
      ? `${INTEGRATION_BASE}/${channel}/pages`
      : null,

  update: (channel: Channel, id: string) =>
    CHANNEL_CONFIG[channel]?.hasUpdate
      ? `${INTEGRATION_BASE}/${channel}/${id}`
      : null,

  delete: (channel: Channel, id: string) =>
    channel === 'sms'
      ? `${integrationsUrl}/twilio/sms/${id}`
      : `${INTEGRATION_BASE}/${channel}/${id}`,

  get: (channel: Channel) =>
    `${INTEGRATION_BASE}/${channel}`,

  
  activateEmail: () => `${integrationsUrl}/email/authenticate`,
  activateSms: () => `${integrationsUrl}/twilio/sms/authenticate`,

  fetchAllIntegration: () => `${INTEGRATION_BASE}/social`,
  updateTikTokSettings: (id: string) => `${INTEGRATION_BASE}/tiktok/${id}`,

};

// 🔹 Main Endpoints
const ENDPOINTS = {
  // Auth
  login: `${authUrl}/login`,

  //Holidays & Events
  unifiedEvents: `${promoflowHubUrl}/events`,
  uploadEvents: `${promoflowHubUrl}/events/upload`,

  // Market Intelligence
  location_demographics: `${promoflowUrl}/location_demographics`,
  menu_suggestions: `${promoflowUrl}/menu_enhancement`,
  broad_competitors: `${promoflowUrl}/broad_competitors`,
  direct_competitors: `${promoflowUrl}/direct_competitors`,
  competitors_promotion: `${promoflowUrl}/competitors_promotion`,
  review_analysis: `${promoflowUrl}/review_analysis`,
  menu: `${franchiseUrl}/menu-item`,

  // Content & Campaigns
  generated_content: `${triggerUrl}/generated_content/filter`,
  generateCampaign: `${triggerUrl}/generate-campaign`,
  schedule_campaign: `${triggerUrl}/schedule-campaign`,
  directCampaign: `${triggerUrl}/direct-campaign`,

  // Flyers
  editFlyer: `${triggerUrl}/generated_content/edit`,
  getFlyer: `${triggerUrl}/flyers`,

  // Restaurant
  franchiseView: `${franchiseUrl}/franchise`,

  // Customers
  customers: `${franchiseUrl}/customer`,

  // Clover
  cloverIntegrate: `${restaurantUrl}/integrate-clover`,
  cloverOauth: `${authUrl}/cloverIntegration?type=promoflow`,
  cloverMoniterUrl: `${restaurantUrl}/task/monitor`,

  //promoflowTemplates
  promoTemplates : `${franchiseUrl}/website`,

  // Campaign V2
  createCampaign: `${promoflowHubUrl}/campaigns`,
  getCampaign: (id: string) => `${promoflowHubUrl}/campaigns/${id}`,
  updateCampaign: (id: string) => `${promoflowHubUrl}/campaigns/${id}`,
  updateCampaignCaption: (id: string) => `${promoflowHubUrl}/campaigns/${id}/flyer-caption`,
  deleteCampaign: (id: string) => `${promoflowHubUrl}/campaigns/${id}`,
  getAllCampaigns: `${promoflowHubUrl}/campaigns`,
  scheduleCampaign: (id: string) => `${promoflowHubUrl}/campaigns/${id}/schedule`,
  deleteSchedule: (id: string) => `${promoflowHubUrl}/campaigns/${id}/schedule`,
  campaignPost: `${INTEGRATION_BASE}/social/campaigns/publish`,
  getCampaignPosts: `${promoflowV2Url}/campaign-posts`,
  deleteCampaignPost: (id: string) => `${promoflowV2Url}/campaign-posts/${id}`,
  mediaCaption: `${promoflowV2Url}/media-caption`,
  analytics: `${promoflowV2Url}/campaign-posts/analytics`,
  channelAnalytics: (channel: string, postId: string, campaignId?: string, startDate?: string) => {
    if (channel.toLowerCase() === 'email' && campaignId && startDate) {
      return `${INTEGRATION_BASE}/${channel}/analytics/campaigns/${campaignId}?startDate=${startDate}`;
    }
    return `${INTEGRATION_BASE}/${channel}/${postId}/analytics`;
  },
  socialAnalyticsSync: `${INTEGRATION_BASE}/social/analytics/sync`,
  workflowTrigger: `${promoflowHubUrl}/campaigns/activate`,
  workflowStatus: `${promoflowV2Url}/system-generated-status`,
  sseMonitor: (requestId: string) => `${sseMonitorUrl}/${requestId}`,
  sseTaskMonitor: (taskId: string, restaurantId: string) => `${sseMonitorUrl}/v1/task/monitor/stream?id=${taskId}&restaurantId=${restaurantId}`,

  // Email Config
  emailConfig: `${integrationsUrl}/integrations/social-media/`,

  // Voice AI
  voiceAI: `${voiceAIUrl}/console/orders`,

  // 🔗 Integrations (NEW CLEAN STRUCTURE)
  integrations: INTEGRATION_ENDPOINTS,
} as const;

export { PROMOFLOW_BASE_API_URL, ENDPOINTS, CHANNEL_CONFIG };
export default ENDPOINTS;